<!-- SkyPlus Case Management Dashboard -->
<div class="sp-dashboard-container">
  <sp-header></sp-header>

  <main class="sp-main">
    <!-- Page Header -->
    <div class="sp-page-header">
      <div class="sp-greeting">
        <h1><span class="material-icons page-icon">gavel</span> Case Management</h1>
        <p>Legal cases, recoveries, hearings & disputes &mdash; one view of everything in flight</p>
      </div>
      <div class="sp-header-actions">
        <button class="sp-refresh-btn" (click)="refresh()" [disabled]="isLoading"
                title="Refresh dashboard">
          <span class="material-icons" [class.spinning]="isLoading">refresh</span>
        </button>
        <button class="btn btn-secondary" (click)="openCaseList()">
          <span class="material-icons">list_alt</span> All Cases
        </button>
        <button class="btn btn-secondary" (click)="exportReport()">
          <span class="material-icons">download</span> Export
        </button>
        <button class="btn btn-primary" (click)="openNewCase()">
          <span class="material-icons">add_circle</span> New Case
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div class="sp-loading" *ngIf="isLoading">
      <span class="material-icons spinning">autorenew</span>
      <p>Loading case data...</p>
    </div>

    <!-- Main content -->
    <div class="case-content" *ngIf="!isLoading">

      <!-- KPI Strip — 7 tiles, the headline number for legal HQ -->
      <section class="sp-stats-section">
        <div class="sp-stat-card" (click)="openCaseList()">
          <div class="sp-stat-icon blue"><span class="material-icons">folder_open</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ formatNumber(stats.activeCases) }}</div>
            <div class="sp-stat-label">Active Cases</div>
          </div>
        </div>

        <div class="sp-stat-card alert" (click)="openCaseList()">
          <div class="sp-stat-icon orange"><span class="material-icons">event_busy</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ stats.hearingsThisWeek }}</div>
            <div class="sp-stat-label">Hearings This Week</div>
          </div>
        </div>

        <div class="sp-stat-card alert-critical" (click)="openCaseList()">
          <div class="sp-stat-icon red"><span class="material-icons">priority_high</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ stats.overdueActions }}</div>
            <div class="sp-stat-label">Overdue Actions</div>
          </div>
        </div>

        <div class="sp-stat-card">
          <div class="sp-stat-icon green"><span class="material-icons">task_alt</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ stats.closedThisMonth }}</div>
            <div class="sp-stat-label">Closed This Month</div>
          </div>
        </div>

        <div class="sp-stat-card">
          <div class="sp-stat-icon purple"><span class="material-icons">fiber_new</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ stats.newThisWeek }}</div>
            <div class="sp-stat-label">New This Week</div>
          </div>
        </div>

        <div class="sp-stat-card wide">
          <div class="sp-stat-icon teal"><span class="material-icons">request_quote</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ formatCurrency(stats.totalExposureKES, true) }}</div>
            <div class="sp-stat-label">Total Exposure (Open)</div>
          </div>
        </div>

        <div class="sp-stat-card wide">
          <div class="sp-stat-icon indigo"><span class="material-icons">savings</span></div>
          <div class="sp-stat-content">
            <div class="sp-stat-value">{{ formatCurrency(stats.recoveredYTDKES, true) }}</div>
            <div class="sp-stat-label">Recovered YTD &middot;
              <span class="recovery-rate">{{ stats.recoveryRatePct }}%</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main grid -->
      <div class="dashboard-grid">

        <!-- ╔══ LEFT COLUMN ══╗ -->
        <div class="grid-left">

          <!-- Upcoming court dates -->
          <section class="card events-card">
            <div class="card-header">
              <span class="material-icons">event</span>
              <h2>Upcoming Court Dates</h2>
              <span class="card-subtitle">Next 14 days</span>
            </div>
            <div class="card-content">
              <div class="events-timeline" *ngIf="upcomingEvents.length > 0">
                <div class="event-row"
                     *ngFor="let ev of upcomingEvents"
                     [class]="urgencyClass(daysUntil(ev.scheduledAt))"
                     (click)="openEventCase(ev)">
                  <div class="event-date-pill">
                    <div class="event-day">{{ ev.scheduledAt | date:'d' }}</div>
                    <div class="event-month">{{ ev.scheduledAt | date:'MMM' }}</div>
                  </div>
                  <div class="event-body">
                    <div class="event-title">
                      <span class="material-icons event-type-icon">{{ EVENT_ICONS[ev.type] }}</span>
                      {{ ev.title }}
                    </div>
                    <div class="event-meta">
                      <span class="event-time">
                        <span class="material-icons">schedule</span>
                        {{ formatEventTime(ev.scheduledAt) }}
                      </span>
                      <span class="event-location">
                        <span class="material-icons">place</span>
                        {{ ev.location }}
                      </span>
                      <span class="event-case">
                        <span class="material-icons">folder</span>
                        {{ ev.caseId }}
                      </span>
                    </div>
                  </div>
                  <div class="event-urgency-tag" *ngIf="daysUntil(ev.scheduledAt) !== null">
                    <ng-container *ngIf="daysUntil(ev.scheduledAt)! < 0">{{ -daysUntil(ev.scheduledAt)! }}d OVERDUE</ng-container>
                    <ng-container *ngIf="daysUntil(ev.scheduledAt)! === 0">TODAY</ng-container>
                    <ng-container *ngIf="daysUntil(ev.scheduledAt)! > 0">in {{ daysUntil(ev.scheduledAt) }}d</ng-container>
                  </div>
                </div>
              </div>
              <div class="empty-state" *ngIf="upcomingEvents.length === 0">
                <span class="material-icons">event_available</span>
                <p>No upcoming court dates in the next 14 days.</p>
              </div>
            </div>
          </section>

          <!-- Cases by status (donut-style breakdown) -->
          <section class="card status-card">
            <div class="card-header">
              <span class="material-icons">pie_chart</span>
              <h2>Cases by Status</h2>
              <span class="card-subtitle">{{ totalCases() }} total</span>
            </div>
            <div class="card-content">
              <div class="status-bars">
                <div class="status-bar-row" *ngFor="let s of statusBreakdown">
                  <div class="status-bar-label">
                    <span class="status-dot" [style.background-color]="s.color"></span>
                    <span class="status-name">{{ s.label }}</span>
                    <span class="status-count">{{ s.count }}</span>
                  </div>
                  <div class="status-bar-track">
                    <div class="status-bar-fill"
                         [style.width.%]="statusPercent(s.count)"
                         [style.background-color]="s.color">
                    </div>
                  </div>
                  <div class="status-pct">{{ statusPercent(s.count) }}%</div>
                </div>
              </div>
            </div>
          </section>

          <!-- Cases by type -->
          <section class="card type-card">
            <div class="card-header">
              <span class="material-icons">category</span>
              <h2>Cases by Type</h2>
              <span class="card-subtitle">Active &amp; Pending</span>
            </div>
            <div class="card-content">
              <div class="type-list">
                <div class="type-item" *ngFor="let t of typeBreakdown">
                  <div class="type-info">
                    <span class="type-name">{{ t.label }}</span>
                    <span class="type-exposure">{{ formatCurrency(t.exposureKES, true) }} exposure</span>
                  </div>
                  <div class="type-count-pill">{{ t.count }}</div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- ╔══ RIGHT COLUMN ══╗ -->
        <div class="grid-right">

          <!-- Needs attention -->
          <section class="card attention-card">
            <div class="card-header">
              <span class="material-icons">notification_important</span>
              <h2>Needs Attention</h2>
              <button class="link-btn" (click)="openCaseList()">View All</button>
            </div>
            <div class="card-content">
              <div class="attention-list">
                <div class="attention-row"
                     *ngFor="let c of needsAttention"
                     (click)="openCase(c)">
                  <div class="attention-priority"
                       [style.background-color]="PRIORITY_COLORS[c.priority]">
                    {{ c.priority.charAt(0) }}
                  </div>
                  <div class="attention-body">
                    <div class="attention-title">{{ c.title }}</div>
                    <div class="attention-meta">
                      <span class="attention-id">{{ c.id }}</span>
                      <span class="attention-type">{{ TYPE_LABELS[c.type] }}</span>
                      <span class="attention-exposure">{{ formatCurrency(c.exposureAmount, true) }}</span>
                    </div>
                  </div>
                  <div class="attention-next" *ngIf="c.nextEventDate">
                    <div class="next-label">Next event</div>
                    <div class="next-time" [class]="urgencyClass(daysUntil(c.nextEventDate))">
                      <ng-container *ngIf="daysUntil(c.nextEventDate)! < 0">{{ -daysUntil(c.nextEventDate)! }}d overdue</ng-container>
                      <ng-container *ngIf="daysUntil(c.nextEventDate)! === 0">Today</ng-container>
                      <ng-container *ngIf="daysUntil(c.nextEventDate)! > 0">in {{ daysUntil(c.nextEventDate) }}d</ng-container>
                    </div>
                  </div>
                </div>
                <div class="empty-state" *ngIf="needsAttention.length === 0">
                  <span class="material-icons">verified</span>
                  <p>All clear &mdash; no cases need immediate attention.</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Recent activity timeline -->
          <section class="card activity-card">
            <div class="card-header">
              <span class="material-icons">history</span>
              <h2>Recent Activity</h2>
              <span class="card-subtitle">Last 10 events</span>
            </div>
            <div class="card-content">
              <div class="activity-list">
                <div class="activity-item" *ngFor="let a of recentActivity">
                  <div class="activity-icon" [class]="activityColorClass(a.category)">
                    <span class="material-icons">{{ activityIcon(a.category) }}</span>
                  </div>
                  <div class="activity-content">
                    <div class="activity-action">
                      <strong>{{ a.actor }}</strong> &middot; {{ a.action }}
                    </div>
                    <div class="activity-details" *ngIf="a.details">{{ a.details }}</div>
                    <div class="activity-meta">
                      <span class="activity-case">{{ a.caseId }}</span>
                      <span class="activity-time">{{ formatTimeAgo(a.timestamp) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Quick actions -->
          <section class="card quick-actions-card">
            <div class="card-header">
              <span class="material-icons">flash_on</span>
              <h2>Quick Actions</h2>
            </div>
            <div class="card-content">
              <div class="quick-actions-grid">
                <button class="quick-action" (click)="openNewCase()">
                  <span class="material-icons">add_circle</span>
                  <span>New Case</span>
                </button>
                <button class="quick-action" (click)="openCaseList()">
                  <span class="material-icons">search</span>
                  <span>Search Cases</span>
                </button>
                <button class="quick-action" (click)="openCaseList()">
                  <span class="material-icons">calendar_month</span>
                  <span>Calendar View</span>
                </button>
                <button class="quick-action" (click)="exportReport()">
                  <span class="material-icons">summarize</span>
                  <span>Run Report</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="last-updated">
        Last updated: {{ lastUpdated | date:'medium' }}
      </div>
    </div>
  </main>
</div>
